import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaExclamationTriangle, FaDownload, FaTrash } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import { ACCOUNT_DELETION_REASON_OPTIONS } from "../../constants/appFeedbackSources";
import styles from "../../styles/pages/profile/AccountDeletion.module.css";

const AccountDeletionSection = ({ user, showToast, logout }) => {
  const oauthOnly = user?.hasPassword === false;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword] = useState("");
  const [reauthCode, setReauthCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deletionReason, setDeletionReason] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  const resetDeleteForm = () => {
    setShowDeleteModal(false);
    setPassword("");
    setReauthCode("");
    setConfirmText("");
    setDeletionReason("");
    setFeedbackComment("");
  };

  const handleExportData = async () => {
    try {
      setIsExporting(true);
      const response = await api.get("/users/me/data/export", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `simpary-data-export-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showToast({ message: "Data exported successfully", type: "success" });
    } catch (error) {
      console.error("Export data error:", error);
      showToast({
        message: error.response?.data?.message || "Failed to export data",
        type: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const submitDeletionFeedback = async () => {
    const reasonOption = ACCOUNT_DELETION_REASON_OPTIONS.find(
      (option) => option.value === deletionReason,
    );
    const trimmedComment = feedbackComment.trim();

    if (!reasonOption && !trimmedComment) {
      return;
    }

    const commentParts = [];
    if (reasonOption) {
      commentParts.push(`Reason: ${reasonOption.label}`);
    }
    if (trimmedComment) {
      commentParts.push(trimmedComment);
    }

    try {
      await api.post("/app-feedback", {
        source: "account_deletion",
        pagePath: window.location.pathname.slice(0, 500),
        comment: commentParts.join("\n\n").slice(0, 1000),
        metadata: {
          reason: reasonOption?.value || "unspecified",
          reasonLabel: reasonOption?.label || undefined,
        },
      });
    } catch (error) {
      // Don't block account deletion if feedback fails
      console.error("Account deletion feedback error:", error);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== "DELETE MY ACCOUNT") {
      showToast({
        message: "Please type 'DELETE MY ACCOUNT' exactly",
        type: "error",
      });
      return;
    }

    if (!oauthOnly && !password) {
      showToast({
        message: "Please enter your password",
        type: "error",
      });
      return;
    }
    if (oauthOnly && !reauthCode.trim()) {
      showToast({
        message: "Enter the confirmation code sent to your email",
        type: "error",
      });
      return;
    }

    try {
      setIsDeleting(true);
      await submitDeletionFeedback();

      await api.delete("/users/me/account", {
        data: {
          password: oauthOnly ? undefined : password,
          reauthCode: oauthOnly ? reauthCode.trim() : undefined,
          confirmText,
        },
      });

      showToast({
        message: "Account marked for deletion (30-day grace period).",
        type: "success",
      });

      if (logout) {
        await logout();
      }
      navigate("/");
    } catch (error) {
      console.error("Delete account error:", error);
      showToast({
        message: error.response?.data?.message || "Failed to delete account",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.dangerZone}>
      <h3 className={styles.dangerTitle}>
        <FaExclamationTriangle /> Danger Zone
      </h3>

      <div className={styles.dangerActions}>
        <div className={styles.actionItem}>
          <div>
            <h4>Export Your Data</h4>
            <p>Download all your data in JSON format (GDPR compliant)</p>
          </div>
          <button
            className="buttonSecondary"
            onClick={handleExportData}
            disabled={isExporting}
          >
            <FaDownload /> {isExporting ? "Exporting..." : "Export Data"}
          </button>
        </div>

        <div className={styles.actionItem}>
          <div>
            <h4>Delete Account</h4>
            <p>Permanently delete your account and all associated data</p>
          </div>
          <button
            className={styles.deleteButton}
            onClick={() => setShowDeleteModal(true)}
          >
            <FaTrash /> Delete Account
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Delete Your Account</h3>
            <p className={styles.warningText}>
              This action is <strong>permanent and irreversible</strong>. All
              your data will be deleted, including:
            </p>
            <ul>
              <li>Your profile and settings</li>
              <li>Your product lists</li>
              <li>Your match history</li>
              <li>Your messages and notifications</li>
            </ul>
            <p className={styles.preservedNote}>
              Your reviews and comments will be anonymized but preserved.
            </p>

            <div className={styles.feedbackSection}>
              <label className={styles.feedbackLabel}>
                Why are you leaving?{" "}
                <span className={styles.optionalTag}>(optional)</span>
              </label>
              <p className={styles.feedbackHint}>
                Your feedback helps us improve Simpary for everyone.
              </p>
              <div
                className={styles.reasonOptions}
                role="group"
                aria-label="Reason for leaving"
              >
                {ACCOUNT_DELETION_REASON_OPTIONS.map((option) => {
                  const selected = deletionReason === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.reasonChip}${
                        selected ? ` ${styles.reasonChipSelected}` : ""
                      }`}
                      onClick={() =>
                        setDeletionReason(selected ? "" : option.value)
                      }
                      aria-pressed={selected}
                      disabled={isDeleting}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <textarea
                className={styles.feedbackTextarea}
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Anything else you'd like us to know? (optional)"
                maxLength={1000}
                rows={3}
                disabled={isDeleting}
              />
            </div>

            <div className={styles.formGroup}>
              {oauthOnly ? (
                <>
                  <label>Confirmation code from your email:</label>
                  <input
                    type="text"
                    value={reauthCode}
                    onChange={(e) => setReauthCode(e.target.value)}
                    placeholder="Code from your email"
                    className={styles.input}
                    disabled={isDeleting}
                    autoComplete="one-time-code"
                  />
                  <button
                    type="button"
                    className="buttonSecondary"
                    disabled={isDeleting || sendingCode}
                    onClick={async () => {
                      try {
                        setSendingCode(true);
                        await api.post("/users/me/reauth-code", { purpose: "delete" });
                        showToast({
                          message: "Confirmation code sent to your email",
                          type: "success",
                        });
                      } catch (error) {
                        showToast({
                          message:
                            error.response?.data?.message ||
                            "Could not send a confirmation code",
                          type: "error",
                        });
                      } finally {
                        setSendingCode(false);
                      }
                    }}
                  >
                    {sendingCode ? "Sending..." : "Email me a code"}
                  </button>
                </>
              ) : (
                <>
                  <label>Enter your password:</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className={styles.input}
                    disabled={isDeleting}
                  />
                </>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Type "DELETE MY ACCOUNT" to confirm:</label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className={styles.input}
                disabled={isDeleting}
              />
            </div>

            <div className={styles.modalActions}>
              <button
                className="buttonSecondary"
                onClick={resetDeleteForm}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={styles.deleteButton}
                onClick={handleDeleteAccount}
                disabled={
                  isDeleting ||
                  confirmText !== "DELETE MY ACCOUNT" ||
                  (oauthOnly ? !reauthCode.trim() : !password)
                }
              >
                {isDeleting ? "Deleting..." : "Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletionSection;
